import { IAttributeDoorsFilter } from "helpers/server-rollup";
import { IAttributeFilters } from "./../../product-collection-toolbar/product-collection-toolbar-filters/types";
import { List } from "immutable";
import { AggFuncs, ATTRIBUTE_GROUP_DELIMITER, UnitsEntryType } from "constants/product-collection";
import { IDiffDeptChecker, ISchemaKey } from "helpers/product-collection";
import { AllocationBag, IAllocationDimension, IAllocationDimensionValue } from "helpers/allocation-dimension";
import {
    getDimensionFilters,
    IDoor,
    legacy_parseDimensionParams,
    SR_CUSTOM_DOOR_GROUPING_DELIMITER,
    isDoorInClassification,
} from "helpers/door-collection";

import { IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import { ISuggestionItem, ISuggestionObject } from "types/product-collection-suggestions";
import { ICurrency } from "./grid-renderers/pinned-row-renderers";
import { ColDef, ColGroupDef, ColumnApi, ColumnGroup } from "ag-grid-community";
import { IGridParamsQueryParam, IPreference } from "./tool-panels";
import memoizeOne from "memoize-one";
import { FeatureFlags } from "helpers/feature-flags";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { Distribution } from "actions/collections/collection-settings";

export enum ColumnTypes {
    DoorBulkQuantity = "door_bulk_quantity",
    AllocationQuantity = "allocation_quantity",
    AllocationDimensionTotal = "allocation_dimension_total",
    ClassificationTotal = "classification_total",
    AllocationDimension = "allocation_dimension",
    AllDimensionTotal = "all_dimension_total",
    NestedDimensionTotal = "nested_dimension_total",
    SizesTotal = "sizes_total",
    TitleColumn = "title_column",
    AttributeColumn = "attribute_column",
    DateColumn = "date_column",
    Metrics = "metrics_column",
}

export enum MetricsTotalType {
    GrandTotalMetric = "grand_total_metric",
    TotalMetric = "total_metric",
}

export interface IProps {
    allowedValueFields?: any;
    collection?: any;
    allocationDimensions?: IAllocationDimension[];
    allocationDimensionGroups?: IAllocationDimension[];
    dates?: IAllocationDimensionValue[];
    doors?: IDoor[];
    doorClassificationSchema?: ISchemaKey[];
    dimensionsPanelApplied: DimensionsPanelReduxState;
    doorGrouping?: string;
    editable?: boolean;
    dataEntryType?: UnitsEntryType;
    getSuggestionData?: () => ISuggestionObject;
    handleFocusOnCellMap?: any;
    onLinkProductToItem?: (rowId: string) => void;
    onOpenProductModal?: (id: string) => void;
    gridParams?: any;
    itemDisplayProperty?: List<any>;
    setSuggestionData?: (data: ISuggestionItem[], storeKey: string) => void;
    allocationEnabled?: boolean;
    onMutateCollectionItem?: (itemsId, index, item, value?) => void;
    onChangeDefaultItemVisibility?: (e?: MouseEvent) => void;
    showDefaultItem?: boolean;
    collectionState?: any;
    reviewPlan?: any;
    onMutateReviewPlan?: (dimensionPath: string[], totalType: any, value: string | number) => void;
    currentAllocationDimensionView?: IPreference[];
    merchantId: string;
    legacyMerchantId: string;
    sizeTableType?: string;
    merchantPurchaseOrderDoorGroups: IPurchaseOrderDoorGroup[];
    diffDeptChecker?: IDiffDeptChecker;
    merchantCurrency?: ICurrency;
    displayPlanMetrics?: boolean;
    displayTotals?: boolean;
    pinnedRowColumnOrder?: any;
    defaultDateFormat?: string;
    isServerSide?: boolean;
    isGridTileView?: boolean;
    collectionId?: string;
    inventoryEnabled?: boolean;
    isRollupMode?: boolean;
    shipmentReviewDetailRenderer?: (params: any) => void;
    allocationBagRef?: {
        allocationBag: AllocationBag;
    };
    flags?: FeatureFlags;
    dimensionToolPanelEnabled?: boolean;
    metricsEnabled?: boolean;
    selectedDistribution?: Distribution;
    isCollapseModeOn?: boolean;
}

export const getColDef = (groupChildren: (ColDef | ColGroupDef)[], colId: string): ColDef | null => {
    const colDef = groupChildren.find((element: ColDef) => element.colId === colId);
    if (colDef) {
        return colDef;
    } else {
        const colDefWithChildren = groupChildren.filter((x: ColGroupDef) => x.children) as ColGroupDef[];
        for (let i = 0; i < colDefWithChildren.length; i++) {
            const innerColDef = getColDef(colDefWithChildren[i].children, colId);
            if (innerColDef) {
                return innerColDef;
            }
        }
        return null;
    }
};

export const gridAggFunctions = {
    [AggFuncs.AVG.toLocaleLowerCase()]: (values) => {
        let sum = 0;
        values.forEach((num) => {
            num = num === "" ? 0 : num;
            sum = sum + parseFloat(num);
        });
        if (isNaN(sum) || !values.length) {
            return "";
        } else {
            return sum / values.length;
        }
    },
    [AggFuncs.SUM.toLocaleLowerCase()]: (values) => {
        let sum = 0;
        values.forEach((num) => {
            num = num === "" ? 0 : num;
            sum = sum + parseFloat(num);
        });
        if (isNaN(sum) || !values.length) {
            return "";
        } else {
            return sum;
        }
    },
    [AggFuncs.SUM_WITH_PERCENT.toLocaleLowerCase()]: (values) => {
        let sum = 0;
        values.forEach((num) => {
            num = num === "" ? 0 : num;
            sum = sum + parseFloat(num);
        });
        if (isNaN(sum) || !values.length) {
            return "";
        } else {
            return sum;
        }
    },
    [AggFuncs.COUNT.toLocaleLowerCase()]: (values) => {
        return values.length;
    },
    [AggFuncs.COUNT_WITH_PERCENT.toLocaleLowerCase()]: (values) => {
        return values.length;
    },
};

/**
 * Returns allocation dimension ids based on supplied gridparams with dimension filters.
 * @param allocationDimensions
 * @param gridParams
 * @param dimensionParams
 * @returns {string[] []} - atmost length 3. Index 0 is string[] of delivery dimensions ids. 1 is door and 2 is size.
 */
export const getNewDimensionFilters = (
    allocationDimensions: IAllocationDimension[],
    gridParams: IGridParamsQueryParam,
    dimensionParams: DimensionsPanelReduxState,
): string[][] => {
    const dimensionFilters: string[][] = getDimensionFilters(gridParams);
    return allocationDimensions.map((allocationDimension, index) => {
        const dimensionFilter = dimensionFilters[index];
        if (
            !dimensionFilter // We don't want hidden classification to override active filter
        ) {
            // Special treatment when it's lowest dimension to handle door grouping
            if (index === allocationDimensions.length - 1) {
                const { attribute, attributeHiddenValues } = legacy_parseDimensionParams(dimensionParams);

                // Exclude dimension values that are not active and not included (aka !inAllocation)
                // Exclude dimension values whose classifications are hidden.
                // Eg: When A+ and A clusters are hidden, doors classified with A+ and A should be excluded.
                return allocationDimension.dimension_values.reduce((acc, dimensionValue) => {
                    let isHiddenClassificationValues;

                    if (Array.isArray(attributeHiddenValues)) {
                        isHiddenClassificationValues = attributeHiddenValues?.includes(attribute);
                    } else if (typeof attributeHiddenValues === "object") {
                        attribute?.split(ATTRIBUTE_GROUP_DELIMITER)?.forEach((element) => {
                            const [elementKey, collectionId] = element.split(SR_CUSTOM_DOOR_GROUPING_DELIMITER);
                            const fieldKey = collectionId ? elementKey : element;

                            // @ts-expect-error
                            isHiddenClassificationValues = attributeHiddenValues[element]?.some((value) => {
                                const field = dimensionValue.fields?.[fieldKey];
                                return field && field.string_value === value && (!collectionId || field[collectionId]);
                            });
                        });
                    }

                    const isInHiddenClassification = attribute && isHiddenClassificationValues;

                    const isInAssortment = dimensionValue.active && dimensionValue.included;

                    if (
                        !acc.includes(dimensionValue.id) && // prevent duplicate id
                        !isInHiddenClassification && // not hidden
                        isInAssortment // in assortment
                    ) {
                        acc.push(dimensionValue.id);
                    }
                    return acc;
                }, []);
            } else {
                return allocationDimension.dimension_values.reduce((acc, dimensionValue) => {
                    const isInAssortment = dimensionValue.active && dimensionValue.included;

                    if (
                        isInAssortment // in assortment
                    ) {
                        acc.push(dimensionValue.id);
                    }
                    return acc;
                }, []);
            }
        } else {
            return dimensionFilter;
        }
    });
};

export const memoizedGetNewDimensionFilters = memoizeOne(
    (
        allocationDimensions: IAllocationDimension[],
        gridParams: IGridParamsQueryParam,
        dimensionParams: DimensionsPanelReduxState,
    ) => getNewDimensionFilters(allocationDimensions, gridParams, dimensionParams),
);

// is there is no attributes filter all doors are returned
export const getDoorsWithinClasifications = (doors: IDoor[], attributeFilters: IAttributeFilters): string[] => {
    return doors.reduce((acc, door) => {
        if (
            isDoorInClassification(door, {
                activeClassification: null,
                hiddenClassificationValues: [],
                attributeFilters,
            })
        ) {
            acc.push(door.id);
        }

        return acc;
    }, []);
};

export const getAttributesDoorsFilter = (doors: IDoor[], attributeFilters: IAttributeFilters) =>
    Object.keys(attributeFilters).reduce<IAttributeDoorsFilter>((acc, key) => {
        acc[key] = getDoorsWithinClasifications(doors, {
            [key]: attributeFilters[key],
        });

        return acc;
    }, {} as IAttributeDoorsFilter);

export const getColumnGroupsAtLevel = (
    columnApi: ColumnApi,
    columnDefs: (ColDef | ColGroupDef)[],
    currentLevel: number,
    targetLevel: number,
): ColumnGroup[] => {
    const groupedColumnDefs = columnDefs.filter((colDef) => {
        if ("children" in colDef) {
            return colDef.children && colDef.children.length > 0;
        }

        return false;
    });

    let aggregatedColumnDefs: ColumnGroup[] = [];
    if (currentLevel === targetLevel) {
        groupedColumnDefs.forEach((groupedColumnDef) => {
            if ("groupId" in groupedColumnDef) {
                const columnGroup = columnApi.getColumnGroup(groupedColumnDef.groupId);
                if (columnGroup) {
                    aggregatedColumnDefs.push(columnGroup);
                }
            }
        });
    } else {
        groupedColumnDefs.forEach((groupedColumnDef) => {
            if ("children" in groupedColumnDef) {
                aggregatedColumnDefs = [
                    ...aggregatedColumnDefs,
                    ...getColumnGroupsAtLevel(columnApi, groupedColumnDef.children, currentLevel + 1, targetLevel),
                ];
            }
        });
    }

    return aggregatedColumnDefs;
};
